import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import L from 'leaflet';
import { MapContainer, Marker, TileLayer, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import LicensePlateTag from 'src/app/components/licensePlateTag/LicesePlateTag';
import { createVehicleIcon, DEFAULT_CENTER, getVehicleLabel } from '../helpers';

const TRACKING_MOVE_ANIMATION_MS = 10000;
const THRESHOLD_DISTANCE = 1000;
const SELECTED_FOLLOW_EPSILON = 0.000001;

const getVehicleKey = vehicle =>
  vehicle?.device_id || vehicle?.device_serial || vehicle?.id || vehicle?.license_plate;

const getVehicleLatLng = vehicle => {
  const lat = Number(vehicle?.lat);
  const lng = Number(vehicle?.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return [lat, lng];
};

const getFollowCenterLatLng = (map, position, offsetY = 0, zoom = map.getZoom()) => {
  if (!map || !position) return null;

  const latLng = L.latLng(position[0], position[1]);
  const markerPoint = map.project(latLng, zoom);
  const nextCenterPoint = markerPoint.subtract([0, offsetY]);

  return map.unproject(nextCenterPoint, zoom);
};

const CMSMapViewport = ({ vehicles, selectedVehicle, mapHeight }) => {
  const map = useMap();
  const hasInitialFocus = useRef(false);
  const previousSelectedKey = useRef(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize();
    }, 120);

    return () => window.clearTimeout(timer);
  }, [map, mapHeight]);

  useEffect(() => {
    if (hasInitialFocus.current) return;
    if (vehicles.length === 0) return;

    const timer = window.setTimeout(() => {
      if (hasInitialFocus.current) return;

      if (vehicles.length === 1) {
        const lat = Number(vehicles[0].lat);
        const lng = Number(vehicles[0].lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          map.setView([lat, lng], 13, { animate: true });
        }
      } else {
        const bounds = L.latLngBounds(vehicles.map(v => [Number(v.lat), Number(v.lng)]));
        map.fitBounds(bounds, { padding: [20, 20], animate: true });
      }
      hasInitialFocus.current = true;
    }, 300);

    return () => window.clearTimeout(timer);
  }, [map, vehicles]);

  useEffect(() => {
    if (!hasInitialFocus.current || !selectedVehicle) return;

    const position = getVehicleLatLng(selectedVehicle);
    if (!position) return;

    const selectedKey = getVehicleKey(selectedVehicle);
    const isNewSelection = selectedKey !== previousSelectedKey.current;
    previousSelectedKey.current = selectedKey;

    if (isNewSelection) {
      map.setView(position, Math.max(map.getZoom(), 18), { animate: false });
    }
  }, [map, selectedVehicle]);

  return null;
};

const SmoothVehicleMarker = ({ vehicle }) => {
  const map = useMap();
  const markerRef = useRef(null);
  const frameRef = useRef(null);
  const vehicleKey = getVehicleKey(vehicle);
  const previousVehicleKey = useRef(vehicleKey);
  const targetPosition = getVehicleLatLng(vehicle);
  const targetLat = targetPosition?.[0];
  const targetLng = targetPosition?.[1];
  const [position, setPosition] = useState(targetPosition);

  useEffect(() => {
    return () => {
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!Number.isFinite(targetLat) || !Number.isFinite(targetLng)) return;

    const isNewVehicle = vehicleKey !== previousVehicleKey.current;
    previousVehicleKey.current = vehicleKey;
    const nextTargetPosition = [targetLat, targetLng];

    if (frameRef.current) {
      window.cancelAnimationFrame(frameRef.current);
    }

    const currentPosition = markerRef.current?.getLatLng();
    const startPosition =
      currentPosition && !isNewVehicle
        ? [currentPosition.lat, currentPosition.lng]
        : nextTargetPosition;

    if (isNewVehicle) {
      markerRef.current?.setLatLng(nextTargetPosition);
      setPosition(nextTargetPosition);
      const followCenter = getFollowCenterLatLng(map, nextTargetPosition);
      if (followCenter) {
        map.setView(followCenter, Math.max(map.getZoom(), 18), { animate: false });
      }
      return;
    }

    const oldLatLng = L.latLng(startPosition[0], startPosition[1]);
    const newLatLng = L.latLng(nextTargetPosition[0], nextTargetPosition[1]);
    const distance = oldLatLng.distanceTo(newLatLng);

    if (distance > THRESHOLD_DISTANCE) {
      markerRef.current?.setLatLng(nextTargetPosition);
      setPosition(nextTargetPosition);
      const followCenter = getFollowCenterLatLng(map, nextTargetPosition);
      if (followCenter) {
        map.setView(followCenter, map.getZoom(), { animate: false });
      }
      return;
    }

    const startTime = performance.now();

    const animate = now => {
      const progress = Math.min((now - startTime) / TRACKING_MOVE_ANIMATION_MS, 1);
      const nextPosition = [
        startPosition[0] + (nextTargetPosition[0] - startPosition[0]) * progress,
        startPosition[1] + (nextTargetPosition[1] - startPosition[1]) * progress
      ];

      markerRef.current?.setLatLng(nextPosition);

      const center = map.getCenter();
      const followCenter = getFollowCenterLatLng(map, nextPosition);
      if (
        followCenter &&
        (
          Math.abs(center.lat - followCenter.lat) > SELECTED_FOLLOW_EPSILON ||
          Math.abs(center.lng - followCenter.lng) > SELECTED_FOLLOW_EPSILON
        )
      ) {
        map.setView(followCenter, map.getZoom(), { animate: false });
      }

      if (progress < 1) {
        frameRef.current = window.requestAnimationFrame(animate);
      } else {
        setPosition(nextTargetPosition);
        frameRef.current = null;
      }
    };

    frameRef.current = window.requestAnimationFrame(animate);
  }, [map, targetLat, targetLng, vehicleKey]);

  if (!position) return null;

  const label = getVehicleLabel(vehicle);

  return (
    <Marker
      ref={markerRef}
      key={vehicleKey}
      position={position}
      icon={createVehicleIcon(vehicle)}
    >
      <LeafletTooltip
        direction="top"
        offset={[0, -25]}
        opacity={1}
        permanent={true}
      >
        <LicensePlateTag
          isSelected={true}
          position={vehicle}
          license_plate={label}
          color="white"
          backgroundColor="#D9042F"
        />
      </LeafletTooltip>
    </Marker>
  );
};

const CMSMapWidget = React.memo(({ vehicles, selectedVehicle, mapHeight }) => {
  const validVehicles = vehicles.filter(vehicle => {
    return Boolean(getVehicleLatLng(vehicle));
  });

  return (
    <Box className="cms-camera-map">
      <Box className="cms-camera-map-body">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={13}
          minZoom={3}
          zoomControl={false}
          style={{ height: '100%', width: '100%' }}
        >
          <CMSMapViewport
            vehicles={validVehicles}
            selectedVehicle={selectedVehicle}
            mapHeight={mapHeight}
          />
          <TileLayer
            url="https://{s}.google.com/vt/lyrs=m&hl=vi&x={x}&y={y}&z={z}"
            subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
            maxZoom={20}
          />
          {selectedVehicle && <SmoothVehicleMarker vehicle={selectedVehicle} />}
        </MapContainer>
      </Box>
    </Box>
  );
});

export default CMSMapWidget;
